import React from 'react';
import styled from 'styled-components';
import { Button } from 'antd';
import { WifiOutlined, ReloadOutlined } from '@ant-design/icons';
import * as Sentry from '@sentry/react';
import { isChunkLoadError } from '../../utils/chunkDiagnostics';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  min-height: 300px;
`;

const IconWrapper = styled.div`
  font-size: 48px;
  color: #faad14;
  margin-bottom: 16px;
`;

const Title = styled.h3`
  margin: 0 0 8px 0;
  font-size: 18px;
  color: #262626;
`;

const Description = styled.p`
  margin: 0 0 24px 0;
  font-size: 14px;
  color: #8c8c8c;
  max-width: 280px;
`;

class ChunkLoadErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      isOffline: !navigator.onLine,
    };
  }

  static getDerivedStateFromError(error) {
    if (isChunkLoadError(error)) {
      return { hasError: true };
    }
    throw error;
  }

  componentDidCatch(error) {
    if (isChunkLoadError(error)) Sentry.captureException(error);
  }

  componentDidMount() {
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }

  componentWillUnmount() {
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
  }

  handleOnline = () => {
    this.setState({ isOffline: false });
    if (this.state.hasError) {
      this.handleRetry();
    }
  };

  handleOffline = () => {
    this.setState({ isOffline: true });
  };

  handleRetry = () => {
    this.setState({ hasError: false });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { isOffline } = this.state;

      return (
        <Container>
          <IconWrapper>
            <WifiOutlined />
          </IconWrapper>
          <Title>
            {isOffline ? 'Nema internet konekcije' : 'Greška pri učitavanju'}
          </Title>
          <Description>
            {isOffline
              ? 'Aplikacija ne može učitati potrebne podatke. Provjerite internet konekciju i pokušajte ponovo.'
              : 'Došlo je do greške pri učitavanju stranice. Pokušajte ponovo.'}
          </Description>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={this.handleRetry}
            disabled={isOffline}
          >
            {isOffline ? 'Čekam konekciju...' : 'Pokušaj ponovo'}
          </Button>
          {isOffline && (
            <Description style={{ marginTop: 16, fontSize: 12 }}>
              Stranica će se automatski osvježiti kada se uspostavi konekcija.
            </Description>
          )}
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ChunkLoadErrorBoundary;
